import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CandidateService } from "@/services/candidateService";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { CheckCircle, XCircle } from "lucide-react";
import { classifyConfirmationOutcome } from "@/utils/confirmationOutcome";
import { useCountdown } from "@/hooks/useCountdown";

/**
 * The page an email's "send me a new one" link points to. Unlike the confirm
 * pages, this POSTs to the resend endpoint on mount, not the finalize
 * endpoint — a resend accepts a token in any state (live, expired, replaced
 * by an earlier resend), so it can recover staged work the confirm page
 * alone could not.
 *
 * "Already confirmed" is a distinct outcome from "sent" here, unlike on the
 * confirm pages: a resend that comes back ALREADY_CONFIRMED sent nothing, so
 * telling the candidate a new link is on its way would be wrong.
 */
const ResendConfirmation = ({ flow }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [outcome, setOutcome] = useState("error"); // "sent" | "already-confirmed" | "error"
  const [result, setResult] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [message, setMessage] = useState("");
  const { secondsLeft, start: startCountdown } = useCountdown();

  const resendFn = flow === "resume-reupload" ? CandidateService.resendResumeReuploadConfirmation : CandidateService.resendCandidateConfirmation;

  const requestResend = useCallback(async () => {
    if (!token) {
      setOutcome("error");
      setErrorCode(null);
      setMessage("Invalid confirmation link.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await resendFn(token);
      setResult(response);
      setErrorCode(null);
      setOutcome("sent");
      setMessage(response?.message || "A new confirmation link is on its way. Please check your inbox.");
    } catch (error) {
      const code = error?.response?.data?.error_code;
      const classified = classifyConfirmationOutcome(code);
      setErrorCode(code);
      setResult(null);

      if (classified.status === "success") {
        setOutcome("already-confirmed");
        setMessage(error?.response?.data?.message || "This application has already been confirmed.");
      } else {
        setOutcome("error");
        setMessage(error?.response?.data?.message || "Couldn't send a new link.");
        if (code === "CONFIRMATION_RESEND_TOO_SOON") {
          startCountdown(error?.response?.data?.data?.retry_after_seconds || 60);
        }
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    requestResend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canRetryAfterCooldown = outcome === "error" && classifyConfirmationOutcome(errorCode).canResend === "cooldown";

  return (
    <div className="min-h-[75vh] flex items-center justify-center px-4 sm:px-6 bg-background-primary">
      <Card className="max-w-md w-full p-8 text-center">
        {loading ? (
          <>
            <div className="loader mx-auto mb-4" />
            <h2 className="text-lg font-semibold">Sending a new confirmation link...</h2>
          </>
        ) : outcome === "sent" ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">New Link Sent</h2>
            <p className="text-text-secondary">{message}</p>

            {result && (
              <div className="mt-4 space-y-1 text-xs text-text-secondary">
                {[result.email_masked, result.alternate_email_masked].filter(Boolean).length > 0 && (
                  <p>Sent to {[result.email_masked, result.alternate_email_masked].filter(Boolean).join(" and ")}</p>
                )}
                {typeof result.expires_in_minutes === "number" && <p>This link expires in {result.expires_in_minutes} minutes.</p>}
                {typeof result.sends_remaining === "number" && (
                  <p>
                    {result.sends_remaining} resend{result.sends_remaining === 1 ? "" : "s"} left.
                  </p>
                )}
              </div>
            )}

            <Button onClick={() => navigate("/")} className="mt-5 bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs">
              Back to homepage
            </Button>
          </>
        ) : outcome === "already-confirmed" ? (
          <>
            <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Already Confirmed</h2>
            <p className="text-text-secondary">{message}</p>

            <Button onClick={() => navigate("/")} className="mt-5 bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs">
              Back to homepage
            </Button>
          </>
        ) : (
          <>
            <XCircle size={64} className="mx-auto text-red-500 mb-4" />
            <h2 className="text-xl text-text-primary font-semibold mb-2">Couldn't Send a New Link</h2>
            <p className="text-text-secondary">{message}</p>

            {canRetryAfterCooldown && (
              <Button onClick={requestResend} disabled={secondsLeft > 0} className="mt-4 bg-button-secondary text-text-base rounded-lg px-4 py-2 text-xs">
                {secondsLeft > 0 ? `Try again in ${secondsLeft}s` : "Try again"}
              </Button>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default ResendConfirmation;
